// @ts-ignore
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { fetchWrapper } from "../../helpers";
import PostContentEditor from "../../components/Editors/PostContentEditor/PostContentEditor";
import LoadingOverlay from "../../components/Overlays/LoadingOverlay"; // путь к компоненту
import PostDistributionForm from "../../components/Forms/PostDistributionForm";
import { useDispatch, useSelector } from "react-redux";
import { distActions } from "../../store";
import { useOrganization } from "../../context/organizationContext";
import {useTranslation} from "react-i18next";
import {ImeanHeader} from "../../components/Header/Header";
import {StickyNavigationHeader} from "../../components/Navigation/StickyNavigationHeader";

const baseUrl = `${process.env.REACT_APP_API_URL}`;

// https://baiterek.gov.kz/ru/pr/news/v-kholdinge-bayterek-proshla-blagotvoritelnaya-aktsiya-den-donora

const DistributePostPage = () => {

    const { t } = useTranslation();
    // Form data from Redux
    const distPostTitle = useSelector((state) => state.dist.dist_post_title);
    const distPostPrompt = useSelector((state) => state.dist.dist_post_prompt);
    const distPostType = useSelector((state) => state.dist.dist_post_type); // 0=link, 1=text

    // Employee data from Redux
    const form = useSelector((state) => state.dist.form);
    const selectedEmployees = useSelector((state) => state.dist.selected_employees);

    // Generation indicator - not used for now
    const [isGenerated, setIsGenerated] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [generatedPosts, setGeneratedPosts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isFirstGeneration, setIsFirstGeneration] = useState(true);

    // Generation timer
    const [elapsedTime, setElapsedTime] = useState(0);
    const [lastGenerationTime, setLastGenerationTime] = useState(null);


    // Organization context
    const organization = useOrganization();

    // Navigation React Router
    const navigate = useNavigate();

    // Dispatch slice actions
    const dispatch = useDispatch();

    // Tested redux here with thunks and other types of actions
    const test_data = useSelector((state) => state.dist.test_data);
    const getTestData = async () => {
        await dispatch(distActions.getTestData());
    };

    // Load employees social media when the page loads
    useEffect(() => {
        dispatch(distActions.fetchEmployeeSocialMedia());
    }, [])

    // Status messages to display in the UI
    const statusMessages = {
        completed: "✅ Done",
        active: "⏳ In progress",
        failed: "❌ Failed",
        queued: "⏸️ Waiting"
    };

    // Track Redis job status
    const jobStatus = useSelector((state) => state.dist.jobStatus);

    // Check if any jobs are still in progress
    const hasActiveJobs = () => {
        return Object.values(jobStatus).some(job =>
            job?.status === 'queued' || job?.status === 'active' || job?.status === 'delayed'
        );
    };

    // Start/stop the timer based on active jobs
    useEffect(() => {
        let interval;
        if (isGenerating || hasActiveJobs()) {
            interval = setInterval(() => {
                setElapsedTime((prev) => prev + 1);
            }, 1000);
        } else {
            if (elapsedTime > 0) {
                setLastGenerationTime(elapsedTime); // store last run duration
            }
            setElapsedTime(0); // reset for next run
        }
        return () => clearInterval(interval);
    }, [isGenerating, hasActiveJobs()]);

    const formatTime = (secs) => {
        const minutes = Math.floor(secs / 60);
        const seconds = secs % 60;
        return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
    };

    const startPollingJob = (emp, jobId) => {
        const interval = setInterval(async () => {
            try {
                const jobRes = await fetchWrapper.get(`${baseUrl}/dist/job/${jobId}`);
                console.log(`Polling for ${emp.fullName} → ${jobRes.state}`);

                if (jobRes?.state === "completed") {
                    clearInterval(interval);
                    dispatch(
                        distActions.setJobStatus({
                            employeeId: emp.id,
                            jobId,
                            status: "completed",
                            result: {
                                result: jobRes.result,
                                id: emp.id,
                                UserId: emp.id,
                            },
                        })
                    );
                    setGeneratedPosts((prev) => [...prev, { ...jobRes.result, userId: emp.id }]);

                    // Check if all jobs are completed to stop generation state
                    setTimeout(() => {
                        if (!hasActiveJobs()) {
                            setIsGenerating(false);
                        }
                    }, 100);
                } else if (jobRes?.state === "failed") {
                    clearInterval(interval);
                    let parsedResult = null;
                    try {
                        parsedResult = typeof jobRes.result === "string" ? JSON.parse(jobRes.result) : jobRes.result;
                    } catch (e) {
                        parsedResult = null;
                    }

                    dispatch(
                        distActions.setJobStatus({
                            employeeId: emp.id,
                            jobId,
                            status: "failed",
                            result: {
                                ...(parsedResult || {}),
                                error: "Post generation failed",
                                id: emp.id,
                                UserId: emp.id,
                            },
                        })
                    );

                    // Check if all jobs are completed to stop generation state
                    setTimeout(() => {
                        if (!hasActiveJobs()) {
                            setIsGenerating(false);
                        }
                    }, 100);
                } else if (jobRes?.state === "active") {
                    // Update status to active if it was queued
                    dispatch(
                        distActions.setJobStatus({
                            employeeId: emp.id,
                            jobId,
                            status: "active",
                        })
                    );
                }
            } catch (e) {
                clearInterval(interval);
                console.error(`Polling error for job ${jobId}`, e);
                dispatch(distActions.setJobStatus({ employeeId: emp.id, status: "failed" }));

                // Check if all jobs are completed to stop generation state
                setTimeout(() => {
                    if (!hasActiveJobs()) {
                        setIsGenerating(false);
                    }
                }, 100);
            }
        }, 3000);
    };

    const handleGenerateWithPolling = async () => {
        if (selectedEmployees.length === 0) {
            alert("Please select employees");
            return;
        }

        const { title, instruction, contentType, postText, postLink, languages } = form;
        const content = contentType === 0 ? postLink : postText;

        if (!content?.trim()) {
            alert("Please provide content in the selected tab (Text or Link)");
            return;
        }

        console.log("\u{1F4DD} Submitting post generation with:", {
            title,
            instruction,
            content,
            contentType,
            model: "gpt-4o-mini",
            temperature: 0.75,
            employees: selectedEmployees.map((e) => e.id),
            languages
        });

        // Reset previous generation data for new generation
        dispatch(distActions.resetJobStatus());
        setGeneratedPosts([]);
        setIsGenerated(false);
        setIsGenerating(true);


        for (const emp of selectedEmployees) {
            dispatch(distActions.clearModeratorPost({ employeeId: emp.id }));
            try {
                const res = await fetchWrapper.post(`${baseUrl}/dist/generate-test-post`, {
                    userId: emp.id,
                    title,
                    article: content,
                    instruction: form.instruction,
                    model: "gpt-4o-mini",
                    temperature: 0.75,
                    contentType: contentType === 0 ? "link" : "text",
                    languages
                });

                console.table({
                    userId: emp.id,
                    title,
                    article: content,
                    instruction: form.instruction,
                    model: "gpt-4o-mini",
                    temperature: 0.75,
                    contentType: contentType === 0 ? "link" : "text",
                });

                const jobId = res?.jobId;
                dispatch(distActions.setJobStatus({ employeeId: emp.id, jobId, status: "queued" }));
                startPollingJob(emp, jobId);
            } catch (err) {
                console.error("Generation failed:", err);
                dispatch(distActions.setJobStatus({ employeeId: emp.id, status: "failed", error: err }));
            }
        }

        setIsGenerated(true);
        // setIsGenerating(false);
    };

    return (
        <>
            <ImeanHeader initialDark={true}></ImeanHeader>
            <StickyNavigationHeader headerTitle={t('postDistribution.postDistributionTagName')}
                                    onBackClick={() => navigate('/moderation')}></StickyNavigationHeader>

        <div className="px-4 md:px-8 lg:px-16 bg-white min-h-screen">


            <LoadingOverlay loading={loading}>
                {/* Use grid on md+ to avoid flex min-width quirks */}
                <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Left Column: sticky on md+, drop h-fit/top-4 */}
                    <div className="w-full md:col-span-1 md:sticky md:top-4 bg-white border rounded-xl p-4 shadow-sm">
                        <PostDistributionForm />
                        <hr />
                        <button
                            onClick={handleGenerateWithPolling}
                            disabled={isGenerating || hasActiveJobs()}
                            className={`mt-4 px-4 py-2 rounded transition-colors w-full ${
                                isGenerating || hasActiveJobs()
                                    ? "bg-gray-400 cursor-not-allowed text-white"
                                    : "bg-[#6c63ff] hover:bg-[#5a52d1] text-white"
                            }`}
                        >
                            {isGenerating || hasActiveJobs() ? t("postDistribution.buttons.generating") : t("postDistribution.buttons.generate")}
                        </button>

                        {/* Progress */}
                        <div className="mt-3 text-sm text-gray-600">
                            {(isGenerating || hasActiveJobs()) && (
                                <div className="flex items-center gap-2">
                                    <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full" />
                                    <span>Generating posts…</span>
                                </div>
                            )}

                            {selectedEmployees.length > 0 && (
                                <>
                                    <div className="mb-1 font-medium text-blue-600">
                                        ⏱ {formatTime(elapsedTime)}
                                    </div>
                                    <div className="mt-2 text-xs text-gray-500">
                                        ⏮ Last run took: {formatTime(lastGenerationTime)}
                                    </div>

                                    {/* Numbered + zebra rows */}
                                    <div className="mt-2 text-xs border rounded-md overflow-hidden">
                                        {selectedEmployees.map((emp, index) => (
                                            <div
                                                key={emp.id}
                                                className={`flex justify-between px-2 py-1 ${
                                                    index % 2 === 0 ? "bg-gray-50" : "bg-gray-100"
                                                }`}
                                            >
                      <span className="truncate">
                        {index + 1}. {emp.fullName}:
                      </span>
                                                <span className="ml-2 shrink-0">
                        {statusMessages[jobStatus[emp.id]?.status]}
                      </span>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Right Column: prevent overflow with min-w-0 */}
                    <div className="w-full md:col-span-2 min-w-0 bg-white border rounded-xl p-4 shadow-sm">
                        <PostContentEditor />
                    </div>
                </div>
            </LoadingOverlay>
        </div>
        </>
    );

};

export default DistributePostPage;
